import React, { useCallback, useEffect, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg } from '../../config/common';
import ShotChart, { ShotSide } from '../../components/shotChart/ShotChart';
import UnlocatedShotQueue from '../../components/shotChart/UnlocatedShotQueue';
import { ShotChartRow } from '../../utils/shotLocation';

const GameShotsPage: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gameId = sessionManager.getGameId();

  const [rows, setRows] = useState<ShotChartRow[]>([]);
  const [periods, setPeriods] = useState<string[]>([]);
  const [players, setPlayers] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [queueSide, setQueueSide] = useState<ShotSide | null>(null);

  const fetchShotChart = useCallback(async () => {
    if (!clientId || !gameId) return;
    setLoading(true);
    try {
      // Both sides — the chart's Us / Them toggle picks which one is drawn.
      const response = await api.get(API_URLS.GET_GAME_SHOT_CHART, {
        params: { clientId, gameId },
      });
      if (response.data?.success) {
        setRows(response.data.data.rows || []);
      } else {
        showErrorMsg(response.data?.message || 'Failed to load shot chart');
      }
    } catch (err) {
      console.error('Error fetching shot chart:', err);
      showErrorMsg('Failed to load shot chart');
    } finally {
      setLoading(false);
    }
  }, [clientId, gameId]);

  useEffect(() => {
    fetchShotChart();
  }, [fetchShotChart]);

  // Periods for the filter dropdown
  useEffect(() => {
    const fetchPeriods = async () => {
      if (!clientId || !seasonId || !gameId) return;
      try {
        const response = await api.get(`${API_URLS.GET_GAME_PERIODS}/${gameId}/periods`, {
          params: { clientId, seasonId },
        });
        if (response.data.success) {
          const data = response.data.data;
          const hp = data.halfPeriod;
          const otFlag = data.otFlag;
          if (hp === true) setPeriods(otFlag ? ['H1', 'H2', 'OT'] : ['H1', 'H2']);
          else setPeriods(otFlag ? ['Q1', 'Q2', 'Q3', 'Q4', 'OT'] : ['Q1', 'Q2', 'Q3', 'Q4']);
        }
      } catch {
        // Non-fatal — filter row simply won't show period options
      }
    };
    fetchPeriods();
  }, [clientId, seasonId, gameId]);

  // Roster for the (Us) player filter
  useEffect(() => {
    const fetchPlayers = async () => {
      if (!clientId || !seasonId || !gameId) return;
      try {
        const response = await api.get(
          `${API_URLS.GET_GAME_PLAYER}?clientId=${clientId}&seasonId=${seasonId}&gameId=${gameId}`,
        );
        if (response.data.success) setPlayers(response.data.data || []);
      } catch {
        // Non-fatal — filter row simply won't show player options
      }
    };
    fetchPlayers();
  }, [clientId, seasonId, gameId]);

  // Snapshot the queue when it opens so placed shots don't shift the list mid-walk.
  const [queueRows, setQueueRows] = useState<ShotChartRow[]>([]);
  const openQueue = (side: ShotSide) => {
    setQueueRows(rows.filter((r) => r.player === side && !r.shotLocation));
    setQueueSide(side);
  };

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight text-[#19133c] flex items-center gap-2">
        Shot Chart
        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#19133c] bg-[#7fe526]">
          BETA
        </span>
      </h1>
      <ShotChart
        rows={rows}
        loading={loading}
        periods={periods}
        players={players}
        mode="game"
        showSideToggle
        onLocateShots={openQueue}
        emptyMessage="No shots recorded for this game yet."
      />

      {clientId && seasonId && (
        <UnlocatedShotQueue
          open={queueSide !== null}
          rows={queueRows}
          clientId={clientId}
          seasonId={seasonId}
          onClose={() => setQueueSide(null)}
          onLocated={(rowId, shotLocation) => {
            setRows((prev) => prev.map((r) => (r._id === rowId ? { ...r, shotLocation } : r)));
          }}
        />
      )}
    </div>
  );
};

export default GameShotsPage;
